/**
 * @file Fetch 請求封裝
 * @description 封裝 HTTP 請求方法，提供統一的 API 呼叫介面，包含認證 token 處理
 */
import fetch from 'isomorphic-fetch';
import ExceptionHandleService from 'utils/exceptionHandler';
import apiResponseHandler from './apiResponseHandler';
import localStorageUtil from 'utils/localStorageUtil';
import LocalStorageKeys from 'constants/localStorageKeys';

const _EHS = new ExceptionHandleService({
  _NAME: 'service/fetch.ts',
  _NOTICE: 'fetchJson'
});

/** Content-Disposition filename 解析用 RegExp */
const FILENAME_REGEX = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

/** Fetch 請求選項介面 */
export interface FetchOptions extends RequestInit {
  headers?: Headers;
}

/**
 * 發送 JSON 格式的 HTTP 請求
 * @param url - 請求的 URL
 * @param options - 請求選項 (headers, method, body 等)
 * @returns Promise 回傳 API 回應資料
 * @description 自動處理 Authorization header 和 Content-Type
 */
const fetchJson = (url: string, options: FetchOptions = {}): Promise<unknown> => {
  try {
    const userInfo = localStorageUtil.getItem(LocalStorageKeys.UserInfo) as {
      accessToken?: string;
      refreshToken?: string;
    } | null;
    const token = userInfo?.accessToken;
    const rToken = userInfo?.refreshToken;
    let requestHeaders: Headers;

    if (token === '' && rToken) {
      requestHeaders =
        options.headers ||
        new Headers({
          'refresh-token': rToken,
          Accept: 'application/json'
        });
    } else {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      requestHeaders = options.headers || new Headers(headers);
    }

    if (
      !requestHeaders.has('Content-Type') &&
      !(options && options.body && options.body instanceof FormData)
    ) {
      requestHeaders.set('Content-Type', 'application/json');
    }

    return fetch(url, { ...options, headers: requestHeaders })
      .then((response: Response) => {
        return apiResponseHandler(response, 'json', url, options);
      })
      .catch((err: Error) => {
        errorNotice(`status: ${err.name} | ${err.message}`);
        _EHS.errorReport(err, '', _EHS._LEVEL.ERROR);
      });
  } catch (error) {
    alert('SYSTEM FETCH ERROR: ' + error);
    _EHS.errorReport(error, 'SYSTEM FETCH ERROR:', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * 發送請求並取得 Blob 格式回應
 * @param url - 請求的 URL
 * @param options - 請求選項
 * @param _type - 回應類型 (預設 'blob')
 * @returns Promise 回傳 Blob 資料或 JSON 資料
 * @description 用於檔案下載等需要 binary 資料的請求
 */
const fetchBlob = (
  url: string,
  options: FetchOptions = {},
  _type = 'blob'
): Promise<Blob | unknown> => {
  try {
    const userInfo = localStorageUtil.getItem(LocalStorageKeys.UserInfo) as {
      accessToken?: string;
    } | null;
    const token = userInfo?.accessToken;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const requestHeaders = options.headers || new Headers(headers);

    if (
      !requestHeaders.has('Content-Type') &&
      !(options && options.body && options.body instanceof FormData)
    ) {
      requestHeaders.set('Content-Type', 'application/json');
    }

    return fetch(url, { ...options, headers: requestHeaders })
      .then((response: Response) => {
        const contentDispositionHeader = response.headers.get('Content-Disposition');
        const contentType = response.headers.get('Content-Type');

        if (contentType && contentType.includes('application/json')) {
          return apiResponseHandler(response, 'json', url, options);
        }

        if (contentDispositionHeader) {
          const matches = FILENAME_REGEX.exec(contentDispositionHeader);
          const filename =
            matches !== null && matches[1]
              ? matches[1].replace(/['"]/g, '')
              : 'downloaded-file.zip';
          console.log('Suggested filename:', filename);
        }
        return response.blob();
      })
      .then(blob => {
        return blob;
      })
      .catch((err: Error) => {
        errorNotice(`status: ${err.name} | ${err.message}`);
        _EHS.errorReport(err, '', _EHS._LEVEL.ERROR);
      });
  } catch (error) {
    alert('SYSTEM FETCH ERROR: ' + error);
    _EHS.errorReport(error, 'SYSTEM FETCH ERROR:', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * 顯示錯誤通知
 * @param msg - 錯誤訊息
 * @description 使用 SweetAlert2 顯示系統錯誤提示
 */
const errorNotice = async (msg: string): Promise<void> => {
  const { default: Swal } = await import('sweetalert2');
  Swal.fire({
    icon: 'error',
    html: `抱歉，系統出現技術問題，我們正在積極著手解決。請幾分鐘後再試一次或請洽系統管理員。<br>${msg ? `(${msg})` : ''}`
  });
};

export { fetchBlob };
export default fetchJson;
